import { useTranslation } from 'react-i18next';

import { CardSkeletons } from '@todo/web-ui/components/card-skeletons';
import { CategoryGuide } from '@todo/web-ui/components/category-guide';
import { CategoryStrip } from '@todo/web-ui/components/category-strip';
import { FeaturedRow } from '@todo/web-ui/components/featured-row';
import { HeroSearch } from '@todo/web-ui/components/hero-search';
import { MoodTiles } from '@todo/web-ui/components/mood-tiles';
import { Page } from '@todo/web-ui/components/page';
import { PageHead } from '@todo/web-ui/components/page-head';
import { SampleCards } from '@todo/web-ui/components/sample-cards';
import { Section } from '@todo/web-ui/components/section';
import { seedScript } from '@todo/web-ui/components/seed-script';
import { TourCard } from '@todo/web-ui/components/tour-card';
import { localePath } from '@todo/web-ui/lib/format';
import { HOME_CATEGORIES_SEED_KEY } from '@todo/web-ui/lib/collections';
import { FEATURED_CARDS, FEATURED_TOURS_QUERY, FEATURED_TOURS_SEED_KEY } from '@/lib/prefetch';
import { useTours } from '@todo/core/hooks/use-tours';
import { useLocale } from '@todo/core/i18n/use-locale';

import css from './home.module.css';

// The home page of the mockup: header over the hero (its photo runs under the
// transparent header, Page `underHeader`), hero with search, category strip,
// Featured This Week — the catalog's best-ranked tours, as on dubai's home —
// then dubai's guide to the categories (CategoryGuide: per category the top
// tour's photo, its live count and a paragraph, `home.guide.<slug>`), Curated
// for Your Mood, footer. No newsletter block: the mockup's "Get the Best of
// Miami in Your Inbox" is dropped (the owner's decision, 2026-11-01).
export default function Home() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const tours = localePath(locale, 'tours');
  // Until the seed has rows (an export without data), the mockup's three cards.
  const { tours: featured, loading } = useTours(FEATURED_TOURS_QUERY, FEATURED_TOURS_SEED_KEY);

  return (
    <Page flush underHeader={<HeroSearch />}>
      <PageHead title={t('home.metaTitle')} description={t('home.metaDescription')} path="" indexable>
        {seedScript(FEATURED_TOURS_SEED_KEY)}
        {seedScript(HOME_CATEGORIES_SEED_KEY)}
      </PageHead>

      <CategoryStrip />

      <Section
        id="featured"
        title={t('home.featuredTitle')}
        eyebrow={t('home.featuredEyebrow')}
        more={{ href: tours, label: t('home.viewAllExperiences') }}
        moreBelowOnMobile>
        <FeaturedRow>
          {featured.length ? (
            featured.map((tour) => <TourCard key={tour.id} tour={tour} featured />)
          ) : loading ? (
            <CardSkeletons count={FEATURED_CARDS} />
          ) : (
            <SampleCards />
          )}
        </FeaturedRow>
      </Section>

      <Section id="guide" title={t('home.guideTitle')}>
        <CategoryGuide />
      </Section>

      <Section
        id="moods"
        title={t('home.moodTitle')}
        eyebrow={t('home.moodEyebrow')}
        more={{ href: tours, label: t('home.viewAllCategories') }}
        moreBelowOnMobile
        className={css.last}>
        <MoodTiles />
      </Section>
    </Page>
  );
}
